<script lang="ts">
  import type { TimerSliderStoryblok } from "$types/components";
  import GridContainer from "$lib/molecules/GridContainer.svelte";
  import GridItem from "$lib/atoms/GridItem.svelte";
  import Image from "$lib/atoms/Image.svelte";
  import AccordionItem from "$lib/molecules/AccordionItem.svelte";
  import { onMount } from "svelte";
  import gsap from "gsap";
  import { getBreakpoint, BREAKPOINTS } from "$lib/utils/breakpoints";
  import { ScrollTrigger } from "gsap/dist/ScrollTrigger";
  import RichText from "./RichText.svelte";

  export let blok: TimerSliderStoryblok;
  export let mediaOnTheLeft = false;
  let currentSlide = 0;
  let sliderTween = null;
  let progressBar = null;
  let wrapper;
  let timerIsActive = true;
  const SLIDER_DURATION = 8;
  let desktopMediaQuery: MediaQueryList;
  let isMobile = true;
  let section;
  let scroller;

  const itemClicked = ({ detail: { index } }) => {
    gsap.to(progressBar[index], {
      width: "0%",
      duration: 0.3,
      ease: "power3.inOut",
    });
    if (timerIsActive) {
      timerIsActive = false;
    }
    sliderTween && sliderTween.kill();
    currentSlide = index;
  };

  onMount(() => {
    progressBar = wrapper.querySelectorAll(".progress-bar");

    desktopMediaQuery = window.matchMedia(
      `(min-width: ${BREAKPOINTS.small}px)`,
    );
    desktopMediaQuery.addEventListener("change", onMediaQueryChange);
    onMediaQueryChange();

    return () => {
      desktopMediaQuery.removeEventListener("change", onMediaQueryChange);
      sliderTween && sliderTween.kill();
      scroller && scroller.kill();
    };
  });

  function onMediaQueryChange() {
    isMobile = !desktopMediaQuery.matches;

    if (!isMobile) {
      scroller = ScrollTrigger.create({
        trigger: section,
        start: "top bottom",
        end: "bottom top",
        onEnter: () => {
          slide(currentSlide);
        },
        onEnterBack: () => {
          slide(currentSlide);
        },
        onLeave: () => {
          gsap.set(progressBar, { width: "0%" });
          sliderTween && sliderTween.kill();
        },
        onLeaveBack: () => {
          gsap.set(progressBar, { width: "0%" });
          sliderTween && sliderTween.kill();
        },
      });
    } else {
      scroller && scroller.kill();
      sliderTween && sliderTween.kill();
    }
  }

  const slide = (index) => {
    sliderTween = gsap.to(progressBar[index], {
      duration: SLIDER_DURATION,
      width: "100%",
      ease: "none",
      onComplete: () => {
        gsap.to(progressBar[index], {
          width: "0%",
          duration: 0.3,
          ease: "power3.inOut",
        });
        const newIndex = (index + 1) % blok.items?.length;
        currentSlide = newIndex;
        slide(newIndex);
      },
    });
  };

  function hoverIn({ detail: { index } }) {
    sliderTween.pause();

    if (currentSlide === index) return;

    if (index !== currentSlide) {
      gsap.to(progressBar[currentSlide], {
        width: "0%",
        duration: 0.3,
        ease: "power3.inOut",
      });
    }

    gsap.to(progressBar[index], {
      width: "100%",
      duration: 0.3,
      ease: "power3.inOut",
    });

    if (!timerIsActive) return;
  }

  function hoverOut({ detail: { index } }) {
    gsap.to(progressBar[index], {
      width: "0%",
      duration: 0.3,
      ease: "power3.inOut",
    });

    if (!timerIsActive) return;

    slide(currentSlide);
  }
</script>

<div bind:this={section} class="slider-timer">
  <GridContainer
    alignItems="center"
    directionMd={mediaOnTheLeft ? "row-reverse" : "row"}
  >
    <GridItem col={12} colMd={5} offset={0} offsetMd={mediaOnTheLeft ? 1 : 0}>
      {#if $$slots.default}
        <slot />
      {/if}
      {#if blok && blok.items}
        <div class="slider-text" bind:this={wrapper}>
          {#each blok.items as slide, index}
            <div class="slide-text">
              <AccordionItem
                on:click={itemClicked}
                on:mouseenter={hoverIn}
                on:mouseleave={hoverOut}
                {index}
                active={currentSlide === index}
              >
                <div class="header" slot="header">
                  <div class="progress">
                    <div class="progress-bar"></div>
                  </div>
                  <h6 class="type:h6-large">{slide.headline}</h6>
                </div>

                <div class="accordion" slot="body">
                  <div class="accordion-inner">
                    {#if typeof slide.text === "string"}
                      <p class="type:body-large">{slide.text}</p>
                    {:else}
                      <RichText
                        class="type:body-large"
                        blok={{ content: slide.text }}
                      />
                    {/if}

                    <div class="media-mobile">
                      <Image
                        media={slide.media}
                        aspect="1"
                        lgSize="50"
                        smSize="100"
                      />
                    </div>
                  </div>
                </div>
              </AccordionItem>
            </div>
          {/each}
        </div>
      {/if}
    </GridItem>
    <GridItem col={12} colMd={6} offset={0} offsetMd={mediaOnTheLeft ? 0 : 1}>
      <div class="slider-image">
        {#if blok && blok.items}
          {#each blok.items as slide, index}
            <div
              class={`slide-image ${currentSlide === index ? "is-active" : ""}`}
            >
              <Image
                media={slide.media}
                aspect={"1"}
                aspectMd="4-5"
                lgSize="50"
                smSize="100"
              />
            </div>
          {/each}
        {/if}
      </div>
    </GridItem>
  </GridContainer>
</div>

<style lang="scss">
  .slider-text {
    .slide-text {
      border-top: 1px solid rgb(var(--color-stroke-dark));
    }

    .header {
      .progress {
        height: 1px;
        width: 100%;
        position: absolute;
        top: -1px;
        left: 0;
        overflow: hidden;

        .progress-bar {
          height: 1px;
          width: 0%;
          background: rgb(var(--color-text-dark));
        }
      }
    }

    .accordion-inner {
      padding-top: fluid(16px, 16px);

      .media-mobile {
        margin-top: 24px;

        @include breakpoint("small") {
          display: none;
        }
      }
    }
  }

  .slider-image {
    aspect-ratio: 4/5;
    position: relative;
    overflow: hidden;
    display: none;

    @include breakpoint("small") {
      display: block;
    }

    .slide-image {
      position: absolute;
      top: 0;
      left: 0;
      height: 100%;
      width: 100%;
      opacity: 0;

      &.is-active {
        opacity: 1;
      }
    }
  }
</style>
